/*
  The metrics strip's due tiles, and Work's landing filter, from ONE place.

  `ds-promotion/010` makes three conditions binding on this feature, and the
  second is the reason this module exists rather than a count living in the
  topbar:

    1. the filter arrives visible and removable in Work, never a hidden query
    2. THE COUNT AND THE FILTERED RESULT ARE THE SAME QUERY
    3. the affordance stays quiet: hover and cursor only, no button chrome

  `026` then records what happened when condition 2 was left to convention in
  the design's own fixture: the tile read from `ShellNavigation`'s literals and
  the landing list read from Work's query, and they drifted so far apart that
  Due soon UNDER-reported by five. Its own words: "the count is not merely
  stale, it is unrelated", and "there is no way for a reader to know which
  number is the real one."

  So the count is not computed here and the filter is not computed in Work.
  BOTH call the same two functions in this file, over the same three
  react-query caches Work's All lens already reads (`workItemsQuery`,
  `planQuery`, `ledgerQuery`). Sharing the cache key is what makes them the same
  query in fact and not just in intention.

  `010`'s charter, ratified by Jesse on 3 Aug, also settles two things that would
  otherwise be judgement calls here:

    - The strip is PER-ENGAGEMENT AND PERSON-AGNOSTIC. Identical for every
      signed-in role, no personal counts, ever. So this reads the `all` lens and
      never `mine`.
    - The due tiles count ALL DATED RECORDS ACROSS ALL PARTIES: work items,
      tasks (standing derived from the work beneath them), and dated ledger
      records. "If the strip currently computes anything narrower, that is a
      defect against this ratification."
*/
import type { LedgerEntry, PlanNode, WorkItemView } from '../api/types'
import { addDays, todayIn } from '../lib/personTime'
import {
  type WorkEntry,
  entryFromRecord,
  entryFromTask,
  entryFromWorkItem,
  itemsUnderTask,
} from './WorkColumns'

export type DueFilter = 'due-today' | 'due-soon'

/** The chip's own words. Neutral, per 010: no new use of the word "ledger". */
export const DUE_LABEL: Record<DueFilter, string> = {
  'due-today': 'Due today',
  'due-soon': 'Due soon',
}

export function isDueFilter(value: string | null | undefined): value is DueFilter {
  return value === 'due-today' || value === 'due-soon'
}

/*
  Today as the `YYYY-MM-DD` string the entries carry, where the PERSON is (RAID
  #482). It read `toISOString`, the UTC date, so from 7pm Central the strip
  counted tomorrow's work as due today.
*/

/**
 * Due today is the day itself. Due soon is the seven days AFTER it, which is
 * 010's "all dated records in the window" read against its own landing
 * decision ("dated records inside seven days").
 *
 * A closed record is never due: it has already happened, and counting it would
 * put work on the strip that nobody can act on.
 */
export function matchesDue(entry: WorkEntry, filter: DueFilter, zone: string, today = new Date()): boolean {
  if (entry.closed || !entry.due) return false
  const from = todayIn(zone, today)
  if (filter === 'due-today') return entry.due === from
  return entry.due > from && entry.due <= addDays(from, 7)
}

/**
 * Every dated record in the engagement at ACCOUNT scope, built with the same
 * three functions Work's All lens builds its rows with. Not a parallel
 * derivation: the same one.
 *
 * The arguments `entryFromWorkItem` takes for display - the subject's name, the
 * directory lookup, the drift signals - are omitted deliberately. None of them
 * can change `due` or `closed`, and passing partial versions of them here would
 * invite somebody to believe this list is renderable. It is for counting and
 * for membership, and it is not the row.
 */
export function accountDatedEntries(
  items: readonly WorkItemView[],
  planNodes: readonly PlanNode[],
  ledger: readonly LedgerEntry[],
): WorkEntry[] {
  const taskEntries = planNodes
    .filter((n) => n.nodeType === 'task')
    .map((node) => entryFromTask(node, itemsUnderTask(node.id, items)))
  const itemEntries = items.map((item) => entryFromWorkItem(item, null))
  const recordEntries = ledger.map((entry) => entryFromRecord(entry))
  return [...itemEntries, ...taskEntries, ...recordEntries]
}

/** The tile's number. The same call Work makes to decide what it shows. */
export function countDue(entries: readonly WorkEntry[], filter: DueFilter, zone: string, today = new Date()): number {
  return entries.filter((e) => matchesDue(e, filter, zone, today)).length
}
